export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string; // ISO
  readMinutes: number;
  author: string;
  tags: string[];
  // Markdown-lite: array of paragraphs (string) or section objects
  content: Array<
    | { type: 'p'; text: string }
    | { type: 'h2'; text: string }
    | { type: 'ul'; items: string[] }
  >;
}

export const blogPosts: BlogPost[] = [
  {
    slug: 'how-to-care-for-vapour-blasted-parts',
    title: 'How to Care for Your Vapour Blasted Parts: Sealing, Protecting & Long-Term Maintenance',
    description:
      'A practical guide to protecting freshly vapour blasted alloy — what products to use (ACF-50, clear coatings, waxes), how to apply them, and how to keep parts looking factory-fresh.',
    date: '2026-05-01',
    readMinutes: 7,
    author: 'Precision Vapour Blasting',
    tags: ['Aftercare', 'ACF-50', 'Clear Coating'],
    content: [
      {
        type: 'p',
        text: 'When your parts come back from vapour blasting they look incredible — that even, satin factory finish is exactly why people choose the process. The catch is that bare alloy is reactive. Aluminium starts oxidising the moment it meets air, and within a few weeks an unprotected blasted surface will dull, spot and eventually grey out completely. The good news is that protecting that finish is simple if you do it early and pick the right product for the job.',
      },
      { type: 'h2', text: 'Why bare alloy needs protecting' },
      {
        type: 'p',
        text: 'Vapour blasting strips every trace of oxidation, oil and old coating from the metal. That cleanliness is what gives the satin look — but it also means there is no protective layer left on the part. Moisture in the air, fingerprints, fuel vapour and road salt will all start attacking the surface immediately. The first 48 hours after blasting are the most critical: that is when you should either fit the part back into a sealed assembly (engine cases, carb internals) or apply a protective product.',
      },
      { type: 'h2', text: 'Option 1: ACF-50 — the easiest protection for working parts' },
      {
        type: 'p',
        text: 'ACF-50 (Anti-Corrosion Formula) is an ultra-thin lanolin-based film originally developed for the aerospace industry. It is our go-to recommendation for motorcycle engine externals, carburettor bodies and any part that lives in a damp or coastal environment. A light wipe-down with a microfibre cloth leaves an invisible film that actively displaces moisture and stops corrosion forming. Reapply every 6–12 months, or after the bike has been ridden in heavy rain.',
      },
      {
        type: 'ul',
        items: [
          'Best for: motorcycle engines, marine fittings, exposed alloy brackets.',
          'Pros: invisible, easy to apply, can be reapplied over itself, food-safe equivalents available.',
          'Cons: slightly oily to the touch — not ideal for parts you handle frequently.',
        ],
      },
      { type: 'h2', text: 'Option 2: Clear coatings (lacquer) for a permanent finish' },
      {
        type: 'p',
        text: 'For parts that are on display — engine covers, intake manifolds, classic car timing covers — a clear coating gives you set-and-forget protection. We recommend a high-temperature clear lacquer like VHT Clear, Eastwood Diamond Clear, or a 2K automotive clear coat for the most durable result. The key is preparation: the surface must be perfectly clean and contaminant-free when the lacquer goes on, which is why coating immediately after blasting (within 24–48 hours) gives the best adhesion.',
      },
      {
        type: 'ul',
        items: [
          'VHT Clear (high temp) — great for engine covers and exhaust-adjacent parts.',
          'Eastwood Diamond Clear — popular with restorers, sprays evenly straight from the can.',
          '2K automotive clear (e.g. Spies Hecker, U-POL) — most durable, requires a spray gun and proper extraction.',
          'Cerakote Clear — ceramic-based, extremely tough, ideal for high-wear parts.',
        ],
      },
      { type: 'h2', text: 'Option 3: Microcrystalline wax for show parts' },
      {
        type: 'p',
        text: 'For classic motorcycle and vintage car restorations where authenticity matters, a microcrystalline wax (Renaissance Wax is the museum-grade standard) gives invisible protection without the plastic sheen of a lacquer. It is what conservators use on historic metalwork. Apply a thin coat with a soft cloth, let it haze, then buff. Refresh every 6 months.',
      },
      { type: 'h2', text: 'Option 4: Light oil for internal and short-term storage' },
      {
        type: 'p',
        text: 'If the part is going straight back into an oil-bathed assembly (gearbox internals, engine internals, fork lowers) a wipe of WD-40, CRC 808 or light engine oil is all you need. For short-term storage between blasting and reassembly, the same products work — just bag the part afterwards to keep dust off.',
      },
      { type: 'h2', text: 'What NOT to do' },
      {
        type: 'ul',
        items: [
          'Do not handle parts with bare hands — fingerprints etch into bare alloy within days. Wear nitrile gloves.',
          'Do not use chrome or metal polish on a vapour blasted finish — it will burnish the surface and destroy the satin look.',
          'Do not leave parts sitting on a damp concrete floor or in an unheated shed over winter without protection.',
          'Do not use silicone-based protectants on anything you plan to paint or coat later — silicone contamination causes fish-eye in paint.',
        ],
      },
      { type: 'h2', text: 'Quick reference: what to use where' },
      {
        type: 'ul',
        items: [
          'Motorcycle engine externals → ACF-50, reapplied yearly.',
          'Show car / display engine parts → 2K clear or Cerakote Clear.',
          'Carburettor bodies → ACF-50 externally, light oil internally.',
          'Marine fittings → ACF-50 or Lanox (lanolin spray), reapplied each season.',
          'Vintage / classic restorations → Renaissance Wax.',
          'Wheels and brake calipers → high-temp 2K clear coat.',
        ],
      },
      { type: 'h2', text: 'Need parts blasted in Christchurch?' },
      {
        type: 'p',
        text: 'We are happy to advise on the best aftercare for your specific job when you drop parts off at our Islington workshop. We service all of Canterbury — Christchurch, Hornby, Rolleston, Rangiora and beyond — and can point you toward the right product for the environment your parts will live in.',
      },
    ],
  },
  {
    slug: 'wet-blasting-vs-sandblasting-alloy-parts',
    title: 'Wet Blasting vs Sandblasting: Why Vapour Wins for Alloy Parts',
    description:
      'Why vapour (wet) blasting beats traditional sandblasting for restoring alloy engine cases, carburettors and motorcycle parts in Christchurch.',
    date: '2026-04-15',
    readMinutes: 5,
    author: 'Precision Vapour Blasting',
    tags: ['Vapour Blasting', 'Alloy Restoration', 'Christchurch'],
    content: [
      {
        type: 'p',
        text: 'If you have ever taken an alloy engine case, carburettor or set of cast wheels to a traditional sandblaster you already know the problem — aggressive dry media leaves a chalky, pitted finish that looks rough up close and traps contaminants. Vapour blasting (also called wet blasting or aqua blasting) solves that problem, and it is the process we specialise in at our Islington, Christchurch workshop.',
      },
      { type: 'h2', text: 'How vapour blasting actually works' },
      {
        type: 'p',
        text: 'Vapour blasting suspends very fine glass or ceramic media in a flow of water and propels the slurry at the part using compressed air. The water cushions every impact, so the media polishes and cleans rather than gouging. The result is an even, satin finish that looks closer to the original factory casting than anything dry blasting can produce.',
      },
      { type: 'h2', text: 'Why it beats dry sandblasting on alloy' },
      {
        type: 'ul',
        items: [
          'No surface pitting — preserves machined faces, threads and gasket surfaces.',
          'No media embedment — water flushes media away instead of hammering it into soft alloy.',
          'No heat build-up — parts stay cool, so there is zero risk of warping thin castings.',
          'Cleans contamination as it goes — oil, carbon and old paint lift off in the same pass.',
          'Gentle enough for delicate items like classic carb bodies and vintage cylinder heads.',
        ],
      },
      { type: 'h2', text: 'When dry blasting is still the right tool' },
      {
        type: 'p',
        text: 'Dry blasting still has a place — heavy structural steel, trailer chassis and weld prep before painting are all good candidates. But for anything alloy, anything with precision surfaces, and anything you want to look genuinely restored rather than just cleaned, vapour blasting is the better answer.',
      },
      { type: 'h2', text: 'Get a quote in Christchurch' },
      {
        type: 'p',
        text: 'We service Christchurch, Hornby, Riccarton, Rolleston, Rangiora and the wider Canterbury region. Drop your parts in or get in touch for a free quote.',
      },
    ],
  },
  {
    slug: 'restoring-classic-motorcycle-engine-parts',
    title: 'Restoring Classic Motorcycle Engine Parts with Vapour Blasting',
    description:
      'A practical guide to vapour blasting classic motorcycle engine parts — what to disassemble, what to mask, and what kind of finish to expect.',
    date: '2026-04-01',
    readMinutes: 6,
    author: 'Precision Vapour Blasting',
    tags: ['Motorcycle Restoration', 'Engine Parts', 'How-To'],
    content: [
      {
        type: 'p',
        text: 'Bringing a classic Japanese or British bike back to life is half mechanical and half cosmetic. The mechanical side you can plan for. The cosmetic side — making 40-year-old alloy castings look factory-fresh again — is where most home restorations stall. Vapour blasting is the shortcut, and we have refined the process over hundreds of bike jobs in our Christchurch workshop.',
      },
      { type: 'h2', text: 'What we can vapour blast' },
      {
        type: 'ul',
        items: [
          'Cylinder heads and barrels',
          'Crankcases and engine covers',
          'Carburettor bodies and float bowls',
          'Wheel hubs and brake calipers',
          'Footpegs, levers and small bracketry',
        ],
      },
      { type: 'h2', text: 'Prep before drop-off' },
      {
        type: 'p',
        text: 'The cleaner and more disassembled your parts arrive, the better the result and the faster the turnaround. Remove all bearings, seals, studs and rubber components where you can. If you cannot remove a stud or insert, let us know — we will mask it. Wash off heavy oil and grease so the blasting media is doing finishing work, not degreasing.',
      },
      { type: 'h2', text: 'What the finish looks like' },
      {
        type: 'p',
        text: 'Expect a uniform, satin-grey finish — bright but not shiny, the same look the parts had the day they left the factory casting line. It is not chrome and it is not polished aluminium. It is the original cast finish, restored.',
      },
      { type: 'h2', text: 'Sealing the result' },
      {
        type: 'p',
        text: 'Bare alloy will slowly oxidise. For parts that will be exposed (engine covers, carb bodies), we recommend a clear lacquer or a wipe of light oil within a few days of blasting. We can advise on what works best for your build.',
      },
      { type: 'h2', text: 'Talk to us about your project' },
      {
        type: 'p',
        text: 'Every bike is different. Send us photos and we will give you a quote and an honest opinion on what is worth blasting and what is not.',
      },
    ],
  },
  {
    slug: 'vapour-blasting-marine-parts-canterbury',
    title: 'Vapour Blasting Marine Parts in Canterbury',
    description:
      'How vapour blasting cleans saltwater corrosion from marine engines, propellers and fittings without damaging delicate surfaces.',
    date: '2026-03-15',
    readMinutes: 4,
    author: 'Precision Vapour Blasting',
    tags: ['Marine', 'Canterbury', 'Corrosion'],
    content: [
      {
        type: 'p',
        text: 'Saltwater is unforgiving. Even a single season can leave outboard powerheads, lower units, propellers and stainless fittings caked in white corrosion and salt deposits. Wire-wheeling and chemical strippers will only get you part of the way — and they often leave the underlying metal in worse shape than when you started. Vapour blasting clears salt corrosion gently and completely.',
      },
      { type: 'h2', text: 'Marine parts we regularly handle' },
      {
        type: 'ul',
        items: [
          'Outboard powerhead castings and gear cases',
          'Aluminium and bronze propellers',
          'Stainless steel fittings, cleats and bow rails',
          'Saltwater-affected alloy fuel tanks',
          'Trailer winches and hardware',
        ],
      },
      { type: 'h2', text: 'Why marine owners prefer vapour blasting' },
      {
        type: 'p',
        text: 'Salt corrosion gets into porosity. Dry blasting drives it deeper. Vapour blasting flushes salt out as it cleans, so the part is genuinely decontaminated — not just cosmetically tidy. That matters for anything you intend to repaint or anodise, because trapped salt will lift coatings within months.',
      },
      { type: 'h2', text: 'Get your parts ready for next season' },
      {
        type: 'p',
        text: 'Boating in Canterbury — Lyttelton, Akaroa, Lake Coleridge — is hard on gear. Drop parts off at our Islington workshop in the off-season and we will turn them around in time for summer.',
      },
    ],
  },
  {
    slug: 'vapor-blasting-vs-vapour-blasting-spelling',
    title: 'Vapor Blasting vs Vapour Blasting: Same Process, Two Spellings',
    description:
      'Vapor blasting, vapour blasting, vapor honing, wet blasting, aqua blasting — what the different names mean, why the spelling differs, and what to search for in New Zealand.',
    date: '2026-08-20',
    readMinutes: 4,
    author: 'Precision Vapour Blasting',
    tags: ['Vapor Blasting', 'Terminology', 'Process'],
    content: [
      {
        type: 'p',
        text: 'We get asked this most weeks, usually by someone who has spent an evening watching American restoration channels: is vapor blasting the same as vapour blasting? Short answer — yes. It is one process with two spellings and a handful of trade names, and the confusion costs people time when they are trying to find somebody local to do the work.',
      },
      { type: 'h2', text: 'Why the spelling differs' },
      {
        type: 'p',
        text: 'American English drops the "u" from words like colour, honour and vapour. So a workshop in Texas advertises vapor blasting, while a workshop in Christchurch, Sydney or Birmingham advertises vapour blasting. Neither spelling refers to a different machine, media or technique. If you searched "vapor blasting near me" and landed here, you are in exactly the right place.',
      },
      { type: 'h2', text: 'The other names for the same job' },
      {
        type: 'ul',
        items: [
          'Vapor honing — the most common US term, especially for engine and carburettor work',
          'Wet blasting — the general industry description',
          'Aqua blasting — used widely in the UK and Europe',
          'Slurry blasting — describes the water-and-media mix being fired',
          'Liquid honing — an older engineering term for the same principle',
          'Dustless blasting — a marketing term, usually for a coarser outdoor variant',
        ],
      },
      {
        type: 'p',
        text: 'All of these describe abrasive suspended in water and delivered at low pressure. The water cushions each particle, which is why the process cleans without etching alloy, opening tolerances or warping thin castings.',
      },
      { type: 'h2', text: 'What it is NOT the same as' },
      {
        type: 'p',
        text: 'Vapor blasting is not sandblasting, soda blasting or bead blasting. Those are dry processes that hit bare metal with no cushioning — fine for a steel trailer chassis, damaging for a cylinder head or a carburettor body. If you were quoted for sandblasting on precision parts, read our comparison of vapour blasting and sandblasting before you commit.',
      },
      { type: 'h2', text: 'What to search for in New Zealand' },
      {
        type: 'p',
        text: 'If you are in Canterbury, searching either spelling will find us. We are at Unit 2, 1 Doric Way, Islington, Christchurch, and we take parts shipped from anywhere in New Zealand. Send photos of what you have and we will quote it — whichever way you spell it.',
      },
    ],
  },
];

export const getPostBySlug = (slug: string) =>
  blogPosts.find((p) => p.slug === slug);
